/**
 * ExecutionDetail (Round 1.3).
 *
 * Renders inside `<Drawer>`. Shows full audit-event payload for an
 * execution row in AuditExplorer — replaces the inline `<pre>` block.
 *
 * Key surfaces (extracted from the M-AUDIT §4 envelope):
 *   - correlation_id (clickable to copy)
 *   - method, decision, latency, status
 *   - actor + WG snapshot
 *   - request / response / decision sub-objects (rendered as JSON)
 */
import { useState, type JSX, type ReactNode } from 'react';
import { Copy } from 'lucide-react';

import type { AuditEventListItem } from '@/api/index.js';

import { Drawer } from '../Drawer.js';

interface ExecutionDetailProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly event: AuditEventListItem | null;
  /** Additional actions to render below the standard fields. */
  readonly extraActions?: ReactNode;
}

export function ExecutionDetail({
  open,
  onClose,
  event,
  extraActions,
}: ExecutionDetailProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  if (!event) {
    return (
      <Drawer open={open} onClose={onClose} eyebrow="Audit event" title="Loading…">
        <p className="subtle">No event selected.</p>
      </Drawer>
    );
  }

  const p = event.payload;
  const method = pickStr(p, ['method']) || event.event_type;
  const actor = pickStr(p, ['actor', 'id']) || '—';
  const decision = pickStr(p, ['decision', 'effect']) || '—';
  const reason = pickStr(p, ['decision', 'reason_code']) || '—';
  const latency = pickStr(p, ['response', 'latency_total_ms']);
  const status = pickStr(p, ['response', 'status']) || '—';
  const correlationId = event.correlation_id;

  function copyCorrelation(): void {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(correlationId).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      });
    }
  }

  return (
    <Drawer open={open} onClose={onClose} eyebrow={`Audit · ${event.event_type}`} title={method}>
      <div className="drawer-stack">
        <KV
          label="Correlation ID"
          value={
            <button
              type="button"
              onClick={copyCorrelation}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'transparent',
                border: 0,
                padding: 0,
                color: 'inherit',
                cursor: 'pointer',
              }}
              title="Copy correlation ID"
            >
              <code>{event.correlation_id}</code>
              <Copy size={13} aria-hidden />
              {copied && <span style={{ fontSize: 12, color: '#5fb27a' }}>copied</span>}
            </button>
          }
        />
        <KV label="Event ID" value={<code>{event.event_id}</code>} />
        <KV label="Time" value={new Date(event.created_at).toLocaleString()} />
        <KV label="Actor" value={<code>{actor}</code>} />
        <KV label="Decision" value={<strong>{decision}</strong>} />
        <KV label="Reason code" value={<code>{reason}</code>} />
        {latency && <KV label="Latency" value={`${latency} ms`} />}
        <KV label="Response status" value={status} />
        <KV label="Delivery state" value={event.delivery_state} />

        <h3 style={{ margin: '14px 0 6px', fontSize: 15 }}>Full payload</h3>
        <pre
          style={{
            margin: 0,
            padding: 12,
            background: 'rgba(0, 0, 0, 0.06)',
            borderRadius: 8,
            fontSize: 13,
            fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
            overflow: 'auto',
            maxHeight: 360,
          }}
        >
          {JSON.stringify(p, null, 2)}
        </pre>
        {extraActions && <div style={{ marginTop: 4 }}>{extraActions}</div>}
      </div>
    </Drawer>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, max-content) 1fr', gap: 12 }}
    >
      <span className="subtle" style={{ fontSize: 13 }}>
        {label}
      </span>
      <span style={{ fontSize: 14 }}>{value}</span>
    </div>
  );
}

function pickStr(p: Record<string, unknown>, path: readonly string[]): string {
  let cur: unknown = p;
  for (const k of path) {
    if (cur && typeof cur === 'object') cur = (cur as Record<string, unknown>)[k];
    else return '';
  }
  return cur === undefined || cur === null ? '' : String(cur);
}
